import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Plus,
  FolderOpen,
  BarChart3,
  TrendingUp,
  Activity,
  ClipboardCheck,
  Settings,
  HelpCircle,
  User,
  LogOut,
  Settings2,
  Bot,
  ScanSearch,
  Radar,
  MessageSquare,
  Shield,
  ArrowLeft,
  Database,
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { NotificationPanel } from "@/components/NotificationPanel";
import { WorkspaceSwitcher } from "@/components/WorkspaceSwitcher";
import { useWorkspace } from "@/hooks/use-workspace";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarHeader,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { openFeedbackDialog } from "@/hooks/use-feedback-dialog";
import { MessageSquarePlus } from "lucide-react";

type NavItem = { title: string; url: string; icon: React.ComponentType<{ className?: string }> };

const adminGroups: { label: string; items: NavItem[] }[] = [
  {
    label: "Overview",
    items: [{ title: "Dashboard", url: "/admin/dashboard", icon: BarChart3 }],
  },
  {
    label: "Users",
    items: [
      { title: "Waitlist", url: "/admin/waitlist", icon: ClipboardCheck },
      { title: "Feedback", url: "/admin/feedback", icon: MessageSquare },
    ],
  },
  {
    label: "Product Config",
    items: [
      { title: "Deal Config", url: "/admin/deals", icon: Settings2 },
      { title: "Scan Library", url: "/admin/scan-library", icon: ScanSearch },
      { title: "AI Agent Hub", url: "/admin/agents", icon: Bot },
    ],
  },
  {
    label: "Market Data",
    items: [
      { title: "Market Data", url: "/admin/market-data", icon: Database },
      { title: "Market Research", url: "/admin/research", icon: Radar },
    ],
  },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const isAdminSpace = currentPath.startsWith("/admin");
  const { workspaces, activeWorkspace, switchWorkspace, createWorkspace } = useWorkspace();

  const [isAdmin, setIsAdmin] = useState(false);
  const [profile, setProfile] = useState<{
    fullName: string;
    email: string;
    avatarUrl: string | null;
  } | null>(null);


  useEffect(() => {
    const checkAdmin = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .eq("role", "admin")
        .maybeSingle();
      setIsAdmin(!!data);
    };
    checkAdmin();
  }, []);

  useEffect(() => {
    const loadProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from("profiles")
        .select("full_name, avatar_url")
        .eq("id", user.id)
        .maybeSingle();
      setProfile({
        fullName: data?.full_name ?? "",
        email: user.email ?? "",
        avatarUrl: data?.avatar_url ?? null,
      });
    };
    loadProfile();
  }, []);

  const SidebarLink = ({ item, isActive }: { item: NavItem; isActive: boolean }) => {
    const content = (
      <SidebarMenuButton asChild isActive={isActive}>
        <NavLink
          to={item.url}
          end
          className="group relative flex items-center gap-3 px-3 py-2 rounded-md text-sidebar-foreground hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--ink))] transition-colors"
          activeClassName="!bg-[hsl(var(--muted))] !text-[hsl(var(--ink))] font-medium"
        >
          <span
            aria-hidden="true"
            className={`absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-[hsl(var(--brand-ochre))] transition-opacity ${isActive ? "opacity-100" : "opacity-0"}`}
          />
          <item.icon className={`w-4 h-4 shrink-0 ${isActive ? "text-[hsl(var(--brand-forest))]" : ""}`} />
          {!collapsed && <span className="text-[13px] truncate font-sans">{item.title}</span>}
        </NavLink>
      </SidebarMenuButton>
    );

    if (collapsed) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent side="right" className="font-mono text-xs">
            {item.title}
          </TooltipContent>
        </Tooltip>
      );
    }
    return content;
  };

  const isItemActive = (url: string) =>
    currentPath === url || currentPath.startsWith(url + "/");

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border bg-[hsl(var(--bone))]">
      {/* Header */}
      <SidebarHeader className="px-4 py-4 space-y-3 border-b border-[hsl(var(--stone-200))]/60">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded bg-[hsl(var(--brand-forest))] flex items-center justify-center shrink-0 shadow-[0_4px_14px_-4px_hsla(256,87%,62%,0.5)]">
            <span className="font-serif italic text-[hsl(var(--bone))] text-sm font-medium">B</span>
          </div>
          {!collapsed && (
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className="font-sans text-[14px] font-semibold tracking-tight text-[hsl(var(--ink))] truncate">
                Brickshare<span className="text-[hsl(var(--brand-ochre))]">.ai</span>
              </span>
              {isAdminSpace && (
                <span className="font-mono text-3xs uppercase tracking-widest px-1.5 py-0.5 rounded bg-[hsl(var(--brand-ochre))]/20 text-[hsl(var(--brand-ochre))] shrink-0">
                  Admin
                </span>
              )}
            </div>
          )}
        </div>
        {isAdminSpace ? (
          <button
            onClick={() => navigate("/deals-v2/dashboard")}
            className={`flex items-center gap-2 w-full rounded-md border border-[hsl(var(--stone-200))] bg-[hsl(var(--paper))] hover:bg-[hsl(var(--muted))] transition-colors ${
              collapsed ? "justify-center p-2" : "px-3 py-2"
            }`}
            title="Go to Portal"
          >
            <ArrowLeft className="w-3.5 h-3.5 text-[hsl(var(--brand-forest))] shrink-0" />
            {!collapsed && (
              <span className="text-xs font-medium text-[hsl(var(--ink))]">Go to Portal</span>
            )}
          </button>
        ) : (
          <WorkspaceSwitcher
            workspaces={workspaces}
            activeWorkspace={activeWorkspace}
            collapsed={collapsed}
            onSwitch={switchWorkspace}
            onCreate={createWorkspace}
          />
        )}
      </SidebarHeader>

      <SidebarContent className="px-2">
        {isAdminSpace ? (
          <>
            {adminGroups.map((group) => (
              <SidebarGroup key={group.label}>
                {!collapsed && (
                  <SidebarGroupLabel className="font-mono text-2xs uppercase tracking-widest text-muted-foreground/60 px-3 mb-1">
                    {group.label}
                  </SidebarGroupLabel>
                )}
                <SidebarGroupContent>
                  <SidebarMenu>
                    {group.items.map((item) => (
                      <SidebarMenuItem key={item.url}>
                        <SidebarLink item={item} isActive={isItemActive(item.url)} />
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))}

            <div className="flex-1" />

            <SidebarSeparator className="my-2" />
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <div className={`flex items-center gap-3 px-3 py-2 ${collapsed ? "justify-center" : ""}`}>
                      <NotificationPanel />
                      {!collapsed && <span className="text-sm text-sidebar-foreground">Notifications</span>}
                    </div>
                  </SidebarMenuItem>
                  {/* Help Center is a user-facing resource — it lives in the Portal only. */}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        ) : (
          <>
            {/* Deals V2 */}
            <SidebarGroup>
              {!collapsed && (
                <SidebarGroupLabel className="font-mono text-2xs uppercase tracking-widest text-muted-foreground/60 px-3 mb-1">
                  Deals V2
                </SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarLink
                      item={{ title: "Dashboard", url: "/deals-v2/dashboard", icon: LayoutDashboard }}
                      isActive={currentPath === "/deals-v2/dashboard"}
                    />
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarLink
                      item={{ title: "Start New Deal", url: "/deals-v2", icon: Plus }}
                      isActive={currentPath === "/deals-v2"}
                    />
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarLink
                      item={{ title: "My Deals", url: "/deals-v2/my-deals", icon: FolderOpen }}
                      isActive={currentPath === "/deals-v2/my-deals"}
                    />
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarLink
                      item={{ title: "Analytics", url: "/deals-v2/analytics", icon: Activity }}
                      isActive={currentPath === "/deals-v2/analytics"}
                    />
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarLink
                      item={{ title: "Reports", url: "/deals-v2/reports", icon: BarChart3 }}
                      isActive={currentPath === "/deals-v2/reports"}
                    />
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarLink
                      item={{ title: "Market Insights", url: "/market-insights", icon: TrendingUp }}
                      isActive={currentPath === "/market-insights"}
                    />
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>


            <div className="flex-1" />

            {/* Utilities */}
            <SidebarSeparator className="my-2" />
            <SidebarGroup>
              {!collapsed && (
                <SidebarGroupLabel className="font-mono text-2xs uppercase tracking-widest text-muted-foreground/60 px-3 mb-1">
                  Utilities
                </SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <div className={`flex items-center gap-3 px-3 py-2 ${collapsed ? "justify-center" : ""}`}>
                      <NotificationPanel />
                      {!collapsed && <span className="text-sm text-sidebar-foreground">Notifications</span>}
                    </div>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarLink
                      item={{ title: "Help Center", url: "/help-center", icon: HelpCircle }}
                      isActive={isItemActive("/help-center")}
                    />
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            {/* Admin Panel entry (admins only) */}
            {isAdmin && (
              <>
                <SidebarSeparator className="my-2" />
                <SidebarGroup>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      <SidebarMenuItem>
                        <SidebarLink
                          item={{ title: "Admin Panel", url: "/admin/dashboard", icon: Shield }}
                          isActive={false}
                        />
                      </SidebarMenuItem>
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              </>
            )}
          </>
        )}
      </SidebarContent>

      {/* Account menu */}
      <SidebarFooter className="px-2 pb-4 border-t border-[hsl(var(--stone-200))]/60 pt-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <AccountMenu
              profile={profile}
              collapsed={collapsed}
              onNavigate={(to) => navigate(to)}
              onFeedback={() => openFeedbackDialog()}
              onSignOut={async () => {
                await supabase.auth.signOut();
                navigate("/auth");
              }}
            />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}

function getInitials(fullName: string, email: string): string {
  const source = fullName?.trim() || email?.trim() || "";
  if (!source) return "?";
  if (fullName?.trim()) {
    const parts = fullName.trim().split(/\s+/).slice(0, 2);
    return parts.map((p) => p.charAt(0).toUpperCase()).join("");
  }
  return source.charAt(0).toUpperCase();
}

interface AccountMenuProps {
  profile: { fullName: string; email: string; avatarUrl: string | null } | null;
  collapsed: boolean;
  onNavigate: (to: string) => void;
  onFeedback: () => void;
  onSignOut: () => void | Promise<void>;
}

function AccountMenu({ profile, collapsed, onNavigate, onFeedback, onSignOut }: AccountMenuProps) {
  const displayName = profile?.fullName?.trim() || profile?.email?.split("@")[0] || "Account";
  const email = profile?.email ?? "";
  const initials = profile ? getInitials(profile.fullName, profile.email) : "?";

  const trigger = (
    <button
      type="button"
      className={`flex items-center gap-2.5 w-full rounded-md hover:bg-[hsl(var(--muted))] transition-colors text-left ${
        collapsed ? "justify-center p-1.5" : "px-2 py-1.5"
      }`}
    >
      <Avatar className="w-7 h-7 shrink-0">
        {profile?.avatarUrl ? <AvatarImage src={profile.avatarUrl} alt={displayName} /> : null}
        <AvatarFallback className="text-2xs font-medium bg-[hsl(var(--brand-forest))] text-[hsl(var(--bone))]">
          {initials}
        </AvatarFallback>
      </Avatar>
      {!collapsed && (
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-xs font-medium text-[hsl(var(--ink))] truncate">
            {displayName}
          </span>
          <span className="text-2xs text-muted-foreground truncate font-mono">
            {email}
          </span>
        </div>
      )}
    </button>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {collapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>{trigger}</TooltipTrigger>
            <TooltipContent side="right" className="font-mono text-xs">
              {displayName}
            </TooltipContent>
          </Tooltip>
        ) : (
          trigger
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-60">
        <div className="px-2 py-1.5">
          <div className="text-sm font-semibold text-[hsl(var(--ink))] truncate">
            {displayName}
          </div>
          <div className="text-xs text-muted-foreground truncate font-mono">
            {email}
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onNavigate("/profile")}>
          <User className="w-4 h-4 mr-2" /> Profile
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onNavigate("/settings")}>
          <Settings className="w-4 h-4 mr-2" /> Settings
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onFeedback()}>
          <MessageSquarePlus className="w-4 h-4 mr-2" /> Send Feedback
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => void onSignOut()}
          className="text-destructive focus:text-destructive"
        >
          <LogOut className="w-4 h-4 mr-2" /> Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
